/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/require-computed-property-dependencies, ember/require-return-from-computed, getter-return */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import {
  filterBy,
  findBy,
  mapBy,
  objectAt,
  rejectBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';
import { TrackedArray } from 'tracked-built-ins';
import EmberObject, {
  defineProperty,
  get,
  action,
  computed,
} from '@ember/object';
import { or, max, map, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import ContactType from 'linced-app/enums/contact-type';
import MedicalConditionType from 'linced-app/enums/medical-condition-type';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { flattenList } from 'linced-app/utils/fields';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { getFieldOptions } from '@linc-technologies/linced-common/utils/fields';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import { matchYearLevel } from 'linced-app/utils/year-levels';

const fieldOptionsReducer = (fields, field) => {
  if (field.dataType === DataType.LIST) {
    if (field.optionsFromEnum) {
      fields[field.key] = getFieldOptions(field.key);
    } else {
      fields[field.key] = field.options;
    }
  } else {
    if (!isEmpty(field.options)) {
      fields[field.key] = field.options;
    } else {
      fields[field.key] = null;
    }
  }
  return fields;
};

export default class ListController extends Controller {
  @action
  setIncludeCurrent(value) {
    this.includeCurrent = value;
  }

  @action
  setIncludeCurrentStaff(value) {
    this.includeCurrentStaff = value;
  }

  @action
  setIncludeFormer(value) {
    this.includeFormer = value;
  }

  @action
  setIncludeFormerStaff(value) {
    this.includeFormerStaff = value;
  }

  @action
  setIncludeNonCurrentConditions(value) {
    this.includeNonCurrentConditions = value;
  }

  @action
  setIncludeNonCurrentMedical(value) {
    this.includeNonCurrentMedical = value;
  }

  @action
  setIncludePre(value) {
    this.includePre = value;
  }

  @action
  setListTitle(value) {
    this.listTitle = value;
  }

  @action
  setSelectedAdditionalContactCount(value) {
    this.selectedAdditionalContactCount = value;
  }

  @action
  setSelectedPrimaryContactCount(value) {
    this.selectedPrimaryContactCount = value;
  }

  @action
  setSelectedSecondaryContactCount(value) {
    this.selectedSecondaryContactCount = value;
  }

  queryParams = [
    'listTitle',
    'showProfileImage',
    'includeFormerStaff',
    'includeCurrentStaff',
    'includeNonCurrentMedical',
    'includeNonCurrentConditions',
  ];

  @service
  csv;

  @service
  session;

  @service
  excel;

  @service
  router;

  ContactType = ContactType;
  DataType = DataType;
  MedicalConditionType = MedicalConditionType;
  PersonType = PersonType;
  matchYearLevel = matchYearLevel;

  // conditionTypeOptions turns the enum into something resembling a dynamic
  // field's options, for use in filtering.
  @computed('MedicalConditionType.values.[]')
  get conditionTypeOptions() {
    return MedicalConditionType.values.map((option) => {
      return {
        valueInt: option.value,
        label: option.label,
      };
    });
  }

  defaultTitle = 'Custom List';
  @tracked listTitle = '';
  @tracked personType = PersonType.LEARNER;
  @tracked
  includeNonCurrentMedical = false;
  @tracked includeNonCurrentConditions = false;

  @computed('includeNonCurrentConditions')
  get medicalConditionsPath() {
    if (this.includeNonCurrentConditions) {
      return 'medicalConditions';
    }
    return 'currentMedicalConditions';
  }

  @tracked
  includeCurrent = true;
  @tracked
  includePre = false;
  @tracked
  includeFormer = false;
  showProfileImage = false;
  @tracked includeFormerStaff = false;
  @tracked includeCurrentStaff = true;
  // TrackedArray, not a plain array: mutated in place (push/splice via
  // selectGroup/removeGroup below) without ever being reassigned, and read
  // via remainingGroups/filteredPeople/sortedPeople which need that
  // mutation to be independently reactive.
  groupFilter = new TrackedArray();

  @computed('model.groups.[]')
  get filteredGroups() {
    return this.model.groups.filter((group) => {
      if (group.type === GroupType.GROUP) {
        // Omit My Groups which the current user isn't a current staff member in.
        let staff = group.get('currentStaff') ?? [];
        return !!findBy(staff, 'personId', this.session.personId);
      }
      if (group.status === NSWStatus.INACTIVE) {
        return false;
      }
      // Include all other groups.
      return true;
    });
  }

  // Plain getter, not @setDiff: groupFilter is TrackedArray-backed, and a
  // classic macro like @setDiff doesn't reliably observe a TrackedArray's
  // autotracked reads the way it does a real EmberArray's.
  get remainingGroups() {
    return this.filteredGroups.filter(
      (group) => !this.groupFilter.includes(group),
    );
  }

  get tableName() {
    return this.listTitle === this.defaultTitle
      ? this.schoolName
      : this.listTitle;
  }

  @alias('session.school.name')
  schoolName;

  // Plain getter, not @computed: reads remainingGroups, itself now a plain
  // getter over TrackedArray-backed state - a classic @computed dependent
  // key can't reliably observe a plain native getter's autotracked reads.
  get sortedRemainingGroups() {
    let groupTypes = uniq(mapBy(this.remainingGroups, 'type')).sort();
    let sortedRemainingGroups = [];
    groupTypes.forEach((groupType) => {
      let semiFilteredGroups = filterBy(
        this.remainingGroups,
        'type',
        groupType,
      );
      let groupings = uniq(mapBy(semiFilteredGroups, 'grouping')).sort();
      groupings.forEach((grouping) => {
        sortedRemainingGroups.push(
          ...filterBy(semiFilteredGroups, 'grouping', grouping).sort(
            naturalSort('label'),
          ),
        );
      });
    });
    return sortedRemainingGroups;
  }

  groupFilterLabel = 'Filter by Group';

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('session.school.{yearLevels,moeYearLevels}', 'jurisdiction')
  get yearLevels() {
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      return this.session.school.moeYearLevels;
    }
    return this.session.school.yearLevels;
  }

  // TrackedArray, not a plain array: same reason as groupFilter above.
  yearLevelFilter = new TrackedArray();

  // selected holds the array of learners used to create a new group with.
  // @tracked + TrackedArray: toggleAll() reassigns this wholesale (needs
  // @tracked), while toggleSelected/removeSelections mutate it in place
  // (needs TrackedArray) - both patterns are genuinely used here.
  @tracked
  selected = new TrackedArray();

  // clearSelection resets the selected
  clearSelection() {
    this.selected.length = 0;
  }

  // Plain getters, not @intersect/@setDiff: selected/sortedPeople are
  // TrackedArray-backed (sortedPeople transitively, via filteredPeople) -
  // classic macros don't reliably observe that.
  // filteredSelectedPeople filters out people not visible in the table
  // from the selected people
  get filteredSelectedPeople() {
    return this.selected.filter((person) => this.sortedPeople.includes(person));
  }

  // selectionsToRemove creates an array that include the
  // selected people no longer visible in the table
  get selectionsToRemove() {
    return this.selected.filter(
      (person) => !this.sortedPeople.includes(person),
    );
  }

  get remainingYearLevels() {
    return this.yearLevels.filter(
      (year) => !this.yearLevelFilter.includes(year),
    );
  }

  @tracked fieldKeys = '';

  @computed('fieldKeys', 'model.fields.[]')
  get fields() {
    return this.selectFields(this.fieldKeys, this.get('model.fields'));
  }

  @computed('fields.@each.{key,dataType}')
  get fieldOptions() {
    return this.fields.reduce(fieldOptionsReducer, {});
  }

  @computed('medicalConditionFields.@each.{key,dataType}')
  get conditionFieldOptions() {
    return this.medicalConditionFields.reduce(fieldOptionsReducer, {});
  }

  @computed('model.caregiverFields.@each.{key,dataType}')
  get caregiverFieldOptions() {
    let fields = this.get('model.caregiverFields');
    if (!isEmpty(fields)) {
      return fields.reduce(fieldOptionsReducer, {});
    }
    return {};
  }

  @computed('model.medicalFields.@each.{key,dataType}')
  get medicalFieldOptions() {
    let fields = this.get('model.medicalFields');
    if (!isEmpty(fields)) {
      return fields.reduce(fieldOptionsReducer, {});
    }
    return {};
  }

  extraFields = Object.freeze([
    {
      key: 'class',
      label: 'Current Class',
      path: 'homeroom.name',
      dataType: DataType.STRING,
    },
    {
      key: 'nextYearsClass',
      label: "Next Year's Class",
      path: 'nextYearsClass',
      dataType: DataType.STRING,
    },
    {
      key: 'previousYearsClass',
      label: "Previous Year's Class",
      path: 'previousYearsClass',
      dataType: DataType.STRING,
    },
    {
      key: 'teachers',
      label: 'Teacher(s)',
      path: 'homeroom.staffNames',
      dataType: DataType.STRING,
    },
    {
      key: 'heroId',
      label: 'Hero ID',
      path: 'heroId',
      dataType: DataType.INTEGER,
    },
    {
      key: 'fundingYearLevel',
      label: 'Funding Year Level',
      path: 'fundingYearLevel',
      dataType: DataType.INTEGER,
    },
    {
      key: 'isAdmin',
      label: 'Is Admin',
      path: 'isAdmin',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'hasHelpdesk',
      label: 'Has Helpdesk Access',
      path: 'hasHelpdeskAccess',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'pageAccessLevel',
      label: 'Page Access Level',
      path: 'pageAccessLevelLabel',
      dataType: DataType.STRING,
    },
    {
      key: 'restrictedFieldLabels',
      label: 'Restricted Field Access',
      path: 'restrictedFieldLabels',
      dataType: DataType.STRING,
    },
    {
      key: 'staffRoleLabels',
      label: 'Additional Permissions',
      path: 'staffRoleLabels',
      dataType: DataType.STRING,
    },
  ]);

  @computed('extraKeys')
  get extra() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.extraKeys)) {
      return fields;
    }

    let keys = this.extraKeys.split('|');

    return this.extraFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  enrolmentFields = Object.freeze([
    {
      key: 'startDate',
      path: 'enrolment.entryDate',
      label: 'First attendance date',
      dataType: DataType.STRING,
    },
    {
      key: 'endDate',
      path: 'enrolment.exitDate',
      label: 'Last attendance date',
      dataType: DataType.STRING,
    },
    {
      key: 'reasonForLeaving',
      path: 'enrolment.departureReasonLabel',
      label: 'Reason for Leaving',
      dataType: DataType.STRING,
    },
    {
      key: 'destinationSchool',
      path: 'enrolment.destinationSchoolName',
      label: 'Destination School',
      dataType: DataType.STRING,
    },
    {
      key: 'postSchoolActivity',
      path: 'enrolment.postSchoolActivityLabel',
      label: 'Post-school Activity',
      dataType: DataType.STRING,
    },
  ]);

  @computed('enrolmentKeys')
  get enrolment() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.enrolmentKeys)) {
      return fields;
    }

    let keys = this.enrolmentKeys.split('|');

    return this.enrolmentFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  @computed('dateCalculationDate')
  get dateCalculationFields() {
    return [
      {
        key: 'ageOnDate',
        sortKey: 'ageOnDate_sort',
        label: 'Age on ' + this.dateCalculationDate,
        dataType: DataType.STRING,
      },
      {
        key: 'monthsAtSchoolOnDate',
        sortKey: 'monthsAtSchoolOnDate',
        label: 'Months at school on ' + this.dateCalculationDate,
        dataType: DataType.INTEGER,
      },
      {
        key: 'weeksAtSchoolOnDateExcludingHolidays',
        sortKey: 'weeksAtSchoolOnDateExcludingHolidays',
        label:
          'Weeks at school on ' +
          this.dateCalculationDate +
          ' (Excluding Holidays)',
        dataType: DataType.INTEGER,
      },
      {
        key: 'weeksAtSchoolOnDateIncludingHolidays',
        sortKey: 'weeksAtSchoolOnDateIncludingHolidays',
        label:
          'Weeks at school on ' +
          this.dateCalculationDate +
          ' (Including Holidays)',
        dataType: DataType.INTEGER,
      },
      {
        key: 'birthday',
        sortKey: 'birthday_sort',
        label: 'Birthday',
        dataType: DataType.STRING,
      },
    ];
  }

  @computed('dateCalculationKeys', 'dateCalculationDate')
  get dateCalculation() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.dateCalculationKeys)) {
      return fields;
    }

    let keys = this.dateCalculationKeys.split('|');

    return this.dateCalculationFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  medicalFields = Object.freeze([
    {
      key: 'relationshipName',
      label: 'Contact type',
      dataType: DataType.STRING,
    },
    {
      key: 'priority',
      label: 'Contact priority',
      dataType: DataType.INTEGER,
    },
    {
      key: 'notes',
      label: 'Notes',
      dataType: DataType.STRING,
    },
    {
      key: 'permissionToContact',
      label: 'Permission to contact',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      dataType: DataType.STRING,
    },
    {
      key: 'endDate',
      label: 'End Date',
      dataType: DataType.STRING,
    },
  ]);

  conditionFields = Object.freeze([
    {
      key: 'type',
      label: 'Type of Condition',
      dataType: DataType.INTEGER,
    },
    {
      key: 'priority',
      label: 'Priority',
      dataType: DataType.INTEGER,
    },
    {
      key: 'description',
      label: 'Description',
      dataType: DataType.STRING,
    },
    {
      key: 'anaphylaxis',
      label: 'Anaphylaxis',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'highRisk',
      label: 'High Risk',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'prescribedMedicationHeld',
      label: 'Prescribed Medication Held Onsite',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'startDate',
      label: 'Start date',
      dataType: DataType.STRING,
    },
    {
      key: 'reviewDate',
      label: 'Review date',
      dataType: DataType.STRING,
    },
    {
      key: 'endDate',
      label: 'End date',
      dataType: DataType.STRING,
    },
  ]);

  contactFields = Object.freeze([
    {
      key: 'relationshipName',
      label: 'Relationship to student',
      dataType: DataType.STRING,
    },
    {
      key: 'priority',
      label: 'Contact priority',
      dataType: DataType.INTEGER,
    },
    {
      key: 'notes',
      label: 'Notes',
      dataType: DataType.STRING,
    },
    {
      key: 'allowedAccess',
      label: 'Allowed access to Hero app',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'receivesSms',
      label: 'Receives Text Messages',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'billPayer',
      label: 'Has Finance Access',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'permissionToContact',
      label: 'Permission to contact',
      dataType: DataType.BOOLEAN,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      dataType: DataType.STRING,
    },
    {
      key: 'endDate',
      label: 'End Date',
      dataType: DataType.STRING,
    },
  ]);

  @computed('medicalContactExtraKeys')
  get medicalContactExtra() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.medicalContactExtraKeys)) {
      return fields;
    }

    let keys = this.medicalContactExtraKeys.split('|');

    return this.medicalFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  @computed('medicalConditionExtraKeys')
  get medicalConditionExtra() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.medicalConditionExtraKeys)) {
      return fields;
    }

    let keys = this.medicalConditionExtraKeys.split('|');

    return this.conditionFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  @computed('primaryContactExtraKeys')
  get primaryContactExtra() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.primaryContactExtraKeys)) {
      return fields;
    }

    let keys = this.primaryContactExtraKeys.split('|');

    return this.contactFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  @computed('secondaryContactExtraKeys')
  get secondaryContactExtra() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.secondaryContactExtraKeys)) {
      return fields;
    }

    let keys = this.secondaryContactExtraKeys.split('|');

    return this.contactFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  @computed('additionalContactExtraKeys')
  get additionalContactExtra() {
    let fields = [];

    // Return early if there are no extra fields to select.
    if (isEmpty(this.additionalContactExtraKeys)) {
      return fields;
    }

    let keys = this.additionalContactExtraKeys.split('|');

    return this.contactFields.filter((field) => {
      return keys.includes(field.key);
    });
  }

  @tracked medicalConditionFieldKeys = '';

  @computed('model.{fields.[],fieldGroups.[]}')
  get allMedicalConditionFields() {
    return this.get('model.fields').filter(
      (item) => get(item, 'group.key') === 'medicalCondition',
    );
  }

  @computed('medicalConditionFieldKeys', 'allMedicalConditionFields.[]')
  get medicalConditionFields() {
    return this.selectFields(
      this.medicalConditionFieldKeys,
      this.allMedicalConditionFields,
    );
  }

  @computed('medicalConditionFields.[]', 'medicalConditionExtra.[]')
  get filteredMedicalConditionFields() {
    if (findBy(this.medicalConditionExtra, 'key', 'type')) {
      // Remove subtype field, as it'll be rendered with the type.
      return rejectBy(this.medicalConditionFields, 'key', 'core:subtype');
    }
    return this.medicalConditionFields;
  }

  @computed('medicalConditionFields.[]')
  get medicalConditionSubtypeField() {
    return findBy(this.medicalConditionFields, 'key', 'core:subtype');
  }

  @tracked medicalContactFieldKeys = '';

  @computed('medicalContactFieldKeys', 'model.medicalFields.[]')
  get medicalContactFields() {
    return this.selectFields(
      this.medicalContactFieldKeys,
      this.get('model.medicalFields'),
    );
  }

  @tracked primaryContactFieldKeys = '';

  @computed('primaryContactFieldKeys', 'model.caregiverFields.[]')
  get primaryContactFields() {
    return this.selectFields(
      this.primaryContactFieldKeys,
      this.get('model.caregiverFields'),
    );
  }

  @tracked secondaryContactFieldKeys = '';

  @computed('secondaryContactFieldKeys', 'model.caregiverFields.[]')
  get secondaryContactFields() {
    return this.selectFields(
      this.secondaryContactFieldKeys,
      this.get('model.caregiverFields'),
    );
  }

  @tracked additionalContactFieldKeys = '';

  @computed('additionalContactFieldKeys', 'model.caregiverFields.[]')
  get additionalContactFields() {
    return this.selectFields(
      this.additionalContactFieldKeys,
      this.get('model.caregiverFields'),
    );
  }

  @computed('model.people.[]', 'medicalConditionsPath')
  get conditionCounts() {
    return this.get('model.people').map((person) => {
      let conditions = person.get(`learner.${this.medicalConditionsPath}`);
      return isEmpty(conditions) ? 0 : conditions.get('length');
    });
  }

  @max('conditionCounts')
  maxConditions;

  @alias('maxConditions')
  numConditionsToShow;

  @map('model.people', function (person) {
    let medicalContacts = person.get('learner.medicalContacts');
    if (this.includeNonCurrentMedical) {
      return isEmpty(medicalContacts) ? 0 : medicalContacts.get('length');
    }
    if (isEmpty(medicalContacts)) {
      return 0;
    }

    return person.get('learner.currentMedicalContacts.length');
  })
  medicalContactCounts;

  @max('medicalContactCounts')
  maxMedicalContacts;

  @map('model.people', function (person) {
    let contacts = person.get('learner.contacts');
    return isEmpty(contacts)
      ? 0
      : filterBy(contacts, 'type', ContactType.PRIMARY).length;
  })
  primaryContactCounts;

  @max('primaryContactCounts')
  maxPrimaryContacts;

  @map('model.people', function (person) {
    let contacts = person.get('learner.contacts');
    return isEmpty(contacts)
      ? 0
      : filterBy(contacts, 'type', ContactType.SECONDARY).length;
  })
  secondaryContactCounts;

  @max('secondaryContactCounts')
  maxSecondaryContacts;

  @map('model.people', function (person) {
    let contacts = person.get('learner.contacts');
    return isEmpty(contacts)
      ? 0
      : filterBy(contacts, 'type', ContactType.EMERGENCY).length;
  })
  additionalContactCounts;

  @max('additionalContactCounts')
  maxAdditionalContacts;

  @tracked
  selectedMedicalContactCount = 0;

  @or('selectedMedicalContactCount', 'maxMedicalContacts')
  numMedicalContactsToShow;

  @tracked
  selectedPrimaryContactCount = 0;

  @or('selectedPrimaryContactCount', 'maxPrimaryContacts')
  numPrimaryContactsToShow;

  @tracked
  selectedSecondaryContactCount = 0;

  @or('selectedSecondaryContactCount', 'maxSecondaryContacts')
  numSecondaryContactsToShow;

  @tracked
  selectedAdditionalContactCount = 0;

  @or('selectedAdditionalContactCount', 'maxAdditionalContacts')
  numAdditionalContactsToShow;

  @tracked
  fieldFilters = EmberObject.create({});
  @tracked
  enrolmentFieldFilters = EmberObject.create({});
  @tracked
  extraFieldFilters = EmberObject.create({});
  @tracked
  conditionFieldFilters = EmberObject.create({});
  @tracked
  conditionExtraFilters = EmberObject.create({});
  medicalFieldFilters = EmberObject.create({});
  medicalExtraFilters = EmberObject.create({});
  @tracked
  contactFieldFilters = EmberObject.create({});
  @tracked
  contactExtraFilters = EmberObject.create({});
  @tracked
  dateCalculationFilters = EmberObject.create({});
  @tracked fieldFilterChanges = 0;

  @computed('fields.[]')
  get sortField() {
    let field = this.fields[0];
    if (!isEmpty(field)) {
      return 'fields.' + field.get('key');
    }
  }

  @tracked sortDir = 'asc';

  @computed('sortField')
  get sortFieldSchema() {
    return findBy(this.fields, 'key', this.sortField.replace('fields.', ''));
  }

  @computed(
    'model.{people.[],dates.[]}',
    'dateCalculation.[]',
    'dateCalculationDate',
  )
  get calculatedDates() {
    if (this.dateCalculation.length) {
      let date = this.dateCalculationDate;
      return this.model.people.reduce((map, person) => {
        let dates = {
          personId: person.id,
        };
        this.dateCalculation.forEach((field) => {
          if (field.sortKey === 'weeksAtSchoolOnDateExcludingHolidays') {
            dates[field.key] = person['weeksAtSchoolOnDate'](
              date,
              this.model.dates,
              true,
            );
          } else if (field.sortKey === 'weeksAtSchoolOnDateIncludingHolidays') {
            dates[field.key] = person['weeksAtSchoolOnDate'](
              date,
              this.model.dates,
            );
          } else {
            dates[field.key] = person[field.key](date);
          }
          if (field.key !== field.sortKey) {
            // This field's display value isn't sortable.
            // Generate a sortable variant.
            dates[`${field.key}_sort`] = person[field.key](date, true);
          }
        });
        map[person.id] = dates;
        return map;
      }, {});
    }
    return {};
  }

  // Plain getter, not @computed: groupFilter/yearLevelFilter are
  // TrackedArray-backed - a classic @computed dependent key doesn't
  // reliably observe their autotracked reads.
  get filteredPeople() {
    let people = this.get('model.people');
    let fieldOptions = this.fieldOptions;
    let caregiverFieldOptions = this.caregiverFieldOptions;
    let medicalFieldOptions = this.medicalFieldOptions;
    let conditionFieldOptions = this.conditionFieldOptions;
    let conditionsPath = this.medicalConditionsPath;

    if (this.personType === PersonType.LEARNER) {
      // Filter Learners by group and year level (where chosen)
      let groups = this.groupFilter;
      let years = this.yearLevelFilter;

      if (!isEmpty(groups)) {
        people = people.filter((person) => {
          return groups.some((group) => {
            return findBy(group.currentLearners, 'personId', person.id);
          });
        });
      }

      if (!isEmpty(years)) {
        people = people.filter((person) => {
          return years.includes('' + person.get('currentYearLevel'));
        });
      }

      // Filter Learners by medical condition fields (if any filters are set)
      let conditionFilters = this.conditionFieldFilters;
      let conditionFilterKeys = Object.keys(conditionFilters);

      // Only filter if there are any filters on condition fields.
      if (!isEmpty(conditionFilterKeys)) {
        people = people.filter((person) => {
          let include = true;

          conditionFilterKeys.forEach((conditionIndex) => {
            if (!include) {
              // Skip checking as we've already filtered this person out.
              return;
            }
            let conditions = person.get(`learner.${conditionsPath}`);
            let condition = objectAt(conditions, conditionIndex);
            let filters = conditionFilters.get(conditionIndex);
            let fields = this.medicalConditionFields;
            fields.forEach((field) => {
              if (!include) {
                // Skip checking as we've already filtered this person out.
                return;
              }

              let key = field.get('key');
              let filter = filters.get(key);
              let filterField = '';
              if (!isEmpty(filter) && filter !== '!') {
                if (isEmpty(condition)) {
                  include = filter === '!*';
                } else {
                  filterField = condition.get(`fields.${key}`);
                  if (isEmpty(filterField)) {
                    include = filter === '!*';
                  } else {
                    let fieldValue = filterField.get('value');
                    let fieldDataType = filterField.get('datatype');
                    include = filterValue(
                      fieldValue,
                      fieldDataType,
                      filter,
                      conditionFieldOptions[key],
                    );
                  }
                }
              }
            });
          });

          return include;
        });
      }

      // Filter Learners by extra condition fields (if any filters are set).
      let conditionExtraFilters = this.conditionExtraFilters;
      let conditionExtraFilterKeys = Object.keys(conditionExtraFilters);

      // Only filter if there are any filters on extra condition fields
      if (!isEmpty(conditionExtraFilterKeys)) {
        people = people.filter((person) => {
          let include = true;

          conditionExtraFilterKeys.forEach((conditionIndex) => {
            if (!include) {
              // Skip checking as we've already filtered this person out.
              return;
            }
            let conditions = person.get(`learner.${conditionsPath}`);
            let condition = objectAt(conditions, conditionIndex);
            let filters = conditionExtraFilters.get(conditionIndex);
            this.conditionFields.forEach((field) => {
              if (!include) {
                // Skip checking as we've already filtered this person out.
                return;
              }

              let key = get(field, 'key');
              let filter = filters.get(key);
              let options;
              if (key === 'type') {
                options = this.conditionTypeOptions;
              }
              if (!isEmpty(filter) && filter !== '!') {
                if (isEmpty(condition)) {
                  include = filter === '!*';
                } else {
                  let fieldValue = condition.get(key);
                  if (fieldValue === null) {
                    fieldValue = '';
                  }
                  let fieldDataType = get(field, 'dataType');
                  include = filterValue(
                    fieldValue,
                    fieldDataType,
                    filter,
                    options,
                  );
                }
              }
            });
          });

          return include;
        });
      }

      // Filter Learners by contact fields (if any filters are set)
      let contactFilters = this.contactFieldFilters;
      let contactFilterKeys = Object.keys(contactFilters);

      // Only bother trying to filter if there are any filters on contact fields
      if (!isEmpty(contactFilterKeys)) {
        people = people.filter((person) => {
          let include = true;

          contactFilterKeys.forEach((type) => {
            if (!include) {
              // Skip checking as we've already filtered this person out.
              return;
            }
            let [contactType, contactIndex] = type.split('_');
            let contacts = person.get(`learner.${contactType}Contacts`);
            let contact = objectAt(contacts, contactIndex);
            let filters = contactFilters.get(type);
            let fields = this.get('model.caregiverFields');
            if (contactType === 'medical') {
              fields = this.get('model.medicalFields');
            }
            fields.forEach((field) => {
              if (!include) {
                // Skip checking as we've already filtered this person out.
                return;
              }

              let key = field.get('key');
              let fieldOptions = caregiverFieldOptions[key];
              if (contactType === 'medical') {
                fieldOptions = medicalFieldOptions[key];
              }
              let groupKey = field.get('group.key');
              let filter = filters.get(key);
              let filterField = '';
              if (!isEmpty(filter) && filter !== '!') {
                if (isEmpty(contact)) {
                  include = filter === '!*';
                } else {
                  let personPath = 'caregiver.fields';
                  if (contactType === 'medical') {
                    personPath = 'medicalPerson.fields';
                  }
                  filterField = contact.get(`${personPath}.${key}`);
                  if (groupKey === 'relationship') {
                    // Relationship fields come from the Contact subrecord, not the Caregiver.
                    filterField = contact.get('fields.' + key);
                  }

                  if (isEmpty(filterField)) {
                    include = filter === '!*';
                  } else {
                    let fieldValue = filterField.get('value');
                    let fieldDataType = filterField.get('datatype');
                    include = filterValue(
                      fieldValue,
                      fieldDataType,
                      filter,
                      fieldOptions,
                    );
                  }
                }
              }
            });
          });

          return include;
        });
      }

      // Filter Learners by extra contact fields (if any filters are set)
      let contactExtraFilters = this.contactExtraFilters;
      let contactExtraFilterKeys = Object.keys(contactExtraFilters);

      // Only bother trying to filter if there are any filters on extra contact fields
      if (!isEmpty(contactExtraFilterKeys)) {
        people = people.filter((person) => {
          let include = true;

          contactExtraFilterKeys.forEach((type) => {
            if (!include) {
              // Skip checking as we've already filtered this person out.
              return;
            }
            let [contactType, contactIndex] = type.split('_');
            let contacts = person.get(`learner.${contactType}Contacts`);
            let contact = objectAt(contacts, contactIndex);
            let filters = contactExtraFilters.get(type);
            this.contactFields.forEach((field) => {
              if (!include) {
                // Skip checking as we've already filtered this person out.
                return;
              }

              let key = get(field, 'key');
              let filter = filters.get(key);
              if (!isEmpty(filter) && filter !== '!') {
                if (isEmpty(contact)) {
                  include = filter === '!*';
                } else {
                  let fieldValue = contact.get(key);
                  if (fieldValue === null) {
                    fieldValue = '';
                  }
                  let fieldDataType = get(field, 'dataType');
                  include = filterValue(fieldValue, fieldDataType, filter);
                }
              }
            });
          });

          return include;
        });
      }
    } else if (this.personType === PersonType.STAFF) {
      //only include former staff
      if (this.includeFormerStaff && !this.includeCurrentStaff) {
        people = people.filter((person) => {
          return mapBy(
            this.model.users.filter((item) => item.disabled),
            'id',
          ).includes(person.userId);
        });
      } else if (!this.includeFormerStaff && this.includeCurrentStaff) {
        people = people.filter((person) => {
          return mapBy(
            this.model.users.filter((item) => !item.disabled),
            'id',
          ).includes(person.userId);
        });
      }
    }
    // Filter (matching) people based on any defined field-level filters
    let filters = this.fieldFilters;
    let extraFilters = this.extraFieldFilters;
    let enrolmentFilters = this.enrolmentFieldFilters;
    let dateCalculationFilters = this.dateCalculationFilters;
    if (
      isEmpty(Object.keys(filters)) &&
      isEmpty(Object.keys(extraFilters)) &&
      isEmpty(Object.keys(enrolmentFilters)) &&
      isEmpty(Object.keys(dateCalculationFilters))
    ) {
      // No field filters - just return the current list of people
      return people;
    }

    if (!isEmpty(Object.keys(filters))) {
      let fields = this.fields;
      people = people.filter((person) => {
        let include = true;
        fields.forEach(function (field) {
          if (!include) {
            // Skip checking as we've already filtered this person out.
            return;
          }
          let key = field.get('key');
          let filter = filters.get(key);
          if (!isEmpty(filter) && filter !== '!') {
            let fieldValue = person.get('fields.' + key + '.value');
            let fieldDataType = person.get('fields.' + key + '.datatype');
            include = filterValue(
              fieldValue,
              fieldDataType,
              filter,
              fieldOptions[key],
            );
          }
        });

        return include;
      });
    }

    // Filter (matching) people based on any defined field-level filters (Extra fields)
    if (!isEmpty(Object.keys(extraFilters))) {
      let extraFields = this.extraFields;
      people = people.filter((person) => {
        let include = true;
        extraFields.forEach(function (field) {
          if (!include) {
            // Skip checking as we've already filtered this person out.
            return;
          }
          let key = field.key;
          let filter = extraFilters.get(key);
          if (!isEmpty(filter) && filter !== '!') {
            let fieldValue = person.get(field.path) ?? '';
            include = filterValue(fieldValue, field.dataType, filter);
          }
        });

        return include;
      });
    }

    // Filter (matching) people based on any defined date calculation filters (Extra fields)
    if (!isEmpty(Object.keys(dateCalculationFilters))) {
      let dateCalculationFields = this.dateCalculationFields;
      let calculatedDates = this.calculatedDates;
      people = people.filter((person) => {
        let include = true;
        dateCalculationFields.forEach(function (field) {
          if (!include) {
            // Skip checking as we've already filtered this person out.
            return;
          }
          let key = field.key;
          let filter = dateCalculationFilters.get(key);
          if (!isEmpty(filter) && filter !== '!') {
            let fieldValue = calculatedDates[person.id][key];
            include = filterValue(fieldValue, field.dataType, filter);
          }
        });

        return include;
      });
    }

    // Filter (matching) people based on any defined field-level filters (Enrolment fields)
    if (isEmpty(Object.keys(enrolmentFilters))) {
      // No enrolment field filters - just return the current list of people
      return people;
    }

    let enrolmentFields = this.enrolmentFields;
    return people.filter((person) => {
      let include = true;
      enrolmentFields.forEach(function (field) {
        if (!include) {
          // Skip checking as we've already filtered this person out.
          return;
        }
        let key = field.key;
        let filter = enrolmentFilters.get(key);
        if (!isEmpty(filter) && filter !== '!') {
          let fieldValue = person.get(field.path) ?? '';
          if (fieldValue === null) {
            fieldValue = '';
          }
          include = filterValue(fieldValue, field.dataType, filter);
        }
      });

      return include;
    });
  }

  // Plain getter, not @computed: reads filteredPeople (itself now a plain
  // getter) plus groupFilter/yearLevelFilter directly (TrackedArray-backed)
  // - a classic @computed dependent key doesn't reliably observe either.
  get sortedPeople() {
    let comparator = (a, b) => {
      let field = this.sortField;
      let result = 0;

      if (isEmpty(field)) {
        return result;
      }

      let aField = '';
      let bField = '';
      let dataType = 1;
      let conditionsPath = this.medicalConditionsPath;

      if (field.charAt(0) === '|') {
        // This is a contact field.  Extract parts: |[contactType]_[pos]|[fieldKey]
        let [, contactKey, contactField, group] = field.split('|');
        let [contactType, pos] = contactKey.split('_');
        if (contactType === 'condition') {
          // Medical condition, not a contact.
          // Get each person's medical condition in this position (if present).
          let aCondition = objectAt(a.get(`learner.${conditionsPath}`), pos);
          let bCondition = objectAt(b.get(`learner.${conditionsPath}`), pos);

          // Get the values from the conditions.
          let valuePath = contactField;
          if (contactField.includes(':')) {
            // Dynamic field.
            // Determine data type from whichever exists (if either does).
            if (!isEmpty(aCondition)) {
              dataType = aCondition.get(contactField + '.datatype');
            } else if (!isEmpty(bCondition)) {
              dataType = bCondition.get(contactField + '.datatype');
            }
            valuePath += '.value';
          } else {
            // Direct property of the condition.
            let meta = findBy(this.conditionFields, 'key', contactField);
            if (!isEmpty(meta)) {
              dataType = meta.dataType;
            }
          }
          aField = isEmpty(aCondition) ? null : aCondition.get(valuePath);
          bField = isEmpty(bCondition) ? null : bCondition.get(valuePath);
        } else {
          let path = `${contactType}Contacts`;
          if (contactType === 'medical' && !this.includeNonCurrentMedical) {
            path = 'currentMedicalContacts';
          }

          // Get each Person's contact in this position (if there is one)
          let aContact = objectAt(a.get(`learner.${path}`), pos);
          let bContact = objectAt(b.get(`learner.${path}`), pos);

          // Get the contacts' field values (defaulting to null if there's no contact)
          let propName = contactField + '.value';
          // Relationship fields come from the Contact subrecord, not the Caregiver.
          if (group !== 'relationship') {
            let prefix = 'caregiver';
            if (contactType === 'medical') {
              prefix = 'medicalPerson';
            }
            propName = `${prefix}.${propName}`;
          }
          if (contactField.startsWith('contact.')) {
            propName = contactField.replace('contact.', '');
          }
          aField = isEmpty(aContact) ? null : aContact.get(propName);
          bField = isEmpty(bContact) ? null : bContact.get(propName);

          // Determine data type from whichever contact exists (if either does); default (set above) is 1 (string)
          if (!isEmpty(aContact)) {
            dataType = aContact.get(contactField + '.datatype');
          } else if (!isEmpty(bContact)) {
            dataType = bContact.get(contactField + '.datatype');
          }
        }
      } else {
        // This field is directly on the Person record.
        // Check if this is a date, "extra" or enrolment field.
        let dateField = findBy(this.dateCalculation, 'key', field);
        if (!isEmpty(dateField)) {
          aField = this.calculatedDates[a.id][dateField.sortKey];
          bField = this.calculatedDates[b.id][dateField.sortKey];
          dataType = DataType.INTEGER;
        } else {
          let meta = findBy(this.extra, 'path', field);
          if (isEmpty(meta)) {
            meta = findBy(this.enrolment, 'path', field);
          }
          if (!isEmpty(meta)) {
            // Sorting on an "extra" or enrolment field.
            aField = a.get(field);
            bField = b.get(field);
            dataType = meta.dataType;
          } else {
            // Sorting on a dynamic field.
            aField = a.get(field + '.value');
            bField = b.get(field + '.value');
            dataType = a.get(field + '.datatype');
            if (dataType === DataType.LIST) {
              aField = isEmpty(aField)
                ? ''
                : flattenList(aField, this.sortFieldSchema);
              bField = isEmpty(bField)
                ? ''
                : flattenList(bField, this.sortFieldSchema);
            }
          }
        }
      }

      // Non-empty always trumps empty, no matter which flavour of empty (null, undefined, empty string, etc)
      if (isEmpty(aField) && !isEmpty(bField)) {
        result = -1;
      } else if (isEmpty(bField) && !isEmpty(aField)) {
        result = 1;
      } else if (isEmpty(bField) && isEmpty(aField)) {
        result = 0;
      } else {
        if (dataType === 1) {
          // Perform case-insensitive comparison on strings (dataType 1)
          aField = aField.toLowerCase().trim();
          bField = bField.toLowerCase().trim();
          result = aField.localeCompare(bField, undefined, {
            numeric: true,
            caseFirst: false,
            sensitivity: 'base',
            ignorePunctuation: false,
          });
        } else {
          if (aField > bField) {
            result = 1;
          } else if (aField < bField) {
            result = -1;
          }
        }
      }

      if (this.sortDir === 'desc') {
        result = -1 * result;
      }

      return result;
    };
    return this.filteredPeople.slice().sort(comparator);
  }

  removeSelections() {
    this.selectionsToRemove.forEach((person) => {
      let pos = this.selected.indexOf(person);
      if (pos > -1) {
        this.selected.splice(pos, 1);
      }
    });
  }

  @action
  filterCurrentContacts(contacts) {
    if (this.includeNonCurrentMedical) {
      return contacts;
    }
    return filterBy(contacts, 'isCurrent', true);
  }

  @action
  selectGroup(group) {
    this.groupFilter.push(group);
  }

  @action
  removeGroup(group) {
    let pos = this.groupFilter.indexOf(group);
    if (pos > -1) {
      this.groupFilter.splice(pos, 1);
    }
  }

  @action
  selectYearLevel(year) {
    this.yearLevelFilter.push(year);
  }

  @action
  removeYearLevel(year) {
    let pos = this.yearLevelFilter.indexOf(year);
    if (pos > -1) {
      this.yearLevelFilter.splice(pos, 1);
    }
  }

  @action
  toggleSelected(person) {
    let pos = this.selected.indexOf(person);
    if (pos > -1) {
      this.selected.splice(pos, 1);
    } else {
      this.selected.push(person);
    }
  }

  @action
  toggleAll() {
    if (this.selected.length >= this.sortedPeople.length) {
      // All or more is already selected, so
      // clear selection.
      this.clearSelection();
    } else {
      // Not all selected; select them all. TrackedArray, not a plain
      // array copy: `selected` needs to stay TrackedArray so subsequent
      // toggleSelected/removeSelections mutations keep being reactive.
      this.selected = new TrackedArray(this.sortedPeople);
    }
  }

  @action
  setFieldFilter(fieldKey, filter) {
    this.fieldFilters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  setExtraFieldFilter(fieldKey, filter) {
    this.extraFieldFilters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  setEnrolmentFieldFilter(fieldKey, filter) {
    this.enrolmentFieldFilters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  setConditionFieldFilter(pos, fieldKey, filter) {
    let conditionKey = `conditionFieldFilters.${pos}`;
    let filters = this.get(conditionKey);
    if (isEmpty(filters)) {
      this.set(conditionKey, EmberObject.create({}));
      filters = this.get(conditionKey);
    }
    filters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  setConditionExtraFilter(pos, fieldKey, filter) {
    let conditionKey = `conditionExtraFilters.${pos}`;
    let filters = this.get(conditionKey);
    if (isEmpty(filters)) {
      this.set(conditionKey, EmberObject.create({}));
      filters = this.get(conditionKey);
    }
    filters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  setContactFieldFilter(type, pos, fieldKey, filter) {
    let contactKey = `contactFieldFilters.${type}_${pos}`;
    let filters = this.get(contactKey);
    if (isEmpty(filters)) {
      this.set(contactKey, EmberObject.create({}));
      filters = this.get(contactKey);
    }
    filters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  setContactExtraFilter(type, pos, fieldKey, filter) {
    let contactKey = `contactExtraFilters.${type}_${pos}`;
    let filters = this.get(contactKey);
    if (isEmpty(filters)) {
      this.set(contactKey, EmberObject.create({}));
      filters = this.get(contactKey);
    }
    filters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  setDateCalculationFieldFilter(fieldKey, filter) {
    this.dateCalculationFilters.set(fieldKey, filter);
    this.incrementProperty('fieldFilterChanges');
  }

  @action
  handleDateCalculation(person, calculation, date) {
    return person[calculation](date);
  }

  @action
  parseCell(cell) {
    // Deep clone the cell, so we don't change the actual cell.
    let clone = cell.cloneNode(true);

    // Replace all title-bearing elements with their titles.
    clone.querySelectorAll('[title]').forEach((el) => {
      let textNode = document.createTextNode(el.title);
      el.parentNode.replaceChild(textNode, el);
    });

    // Remove sort direction icons
    clone
      .querySelectorAll('.md-sort-icon')
      .forEach((el) => el.parentNode.removeChild(el));

    // Prepend prefix if this is a header row which specifies one
    if (
      clone.tagName.toLowerCase() === 'th' &&
      clone.hasAttribute('data-prefix')
    ) {
      clone.innerText =
        clone.getAttribute('data-prefix') + ' ' + clone.innerText;
    }

    // Return the element's innerText, with all excess whitespace removed.
    return clone.innerText.replace(/\s+/g, ' ').trim();
  }

  selectFields(rawKeys, allFields) {
    let fields = [];

    // Return early if there are no fields to select (or to select from).
    if (isEmpty(rawKeys) || isEmpty(allFields)) {
      return fields;
    }

    let keys = rawKeys.split('|');

    keys.forEach(function (key) {
      let field = allFields.find((f) => f.key === key);
      if (!isEmpty(field)) {
        fields.push(field);
      }
    });

    return fields;
  }

  init() {
    super.init(...arguments);
    this.listTitle = this.defaultTitle;
  }

  reset() {
    // Reset field filters
    this.fieldFilters = EmberObject.create({});
    this.extraFieldFilters = EmberObject.create({});
    this.enrolmentFieldFilters = EmberObject.create({});
    this.conditionFieldFilters = EmberObject.create({});
    this.conditionExtraFilters = EmberObject.create({});
    this.contactFieldFilters = EmberObject.create({});
    this.contactExtraFilters = EmberObject.create({});
    this.dateCalculationFilters = EmberObject.create({});
    // Reset change counter
    this.fieldFilterChanges = 0;
    // Reset sort order
    this.sortDir = 'asc';
    // Reset report title
    this.listTitle = this.defaultTitle;
    // Reset current medical filters
    this.includeNonCurrentMedical = false;
    this.includeNonCurrentConditions = false;
    // Reset selected number of contacts
    this.selectedMedicalContactCount = 0;
    this.selectedPrimaryContactCount = 0;
    this.selectedSecondaryContactCount = 0;
    this.selectedAdditionalContactCount = 0;
    // Rebuild computed property for initial sort field
    defineProperty(
      this,
      'sortField',
      computed('fields.[]', function () {
        let field = this.fields[0];
        if (!isEmpty(field)) {
          return 'fields.' + field.get('key');
        }
      }),
    );
  }
}
